import {gql} from '@apollo/client';
import {CSSProperties, StyleDeclarationMap, StyleSheet, css} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {ActionMethod, ActionOrigin, MODBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  COMPLEX_POLYMER_DISPLAY_NAMES,
  ComplexPolymerAttachmentStyle,
  ComplexPolymerType,
  HELMPolymerType,
  ResolvedAttachmentGraphQL,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {getPolymerTypeFromRef} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {ConcatenateMolfilesQuery} from 'modbio/complex-polymers/tabs/__generated/ConcatenateMolfilesQuery';
import {
  AttachConjugateState,
  SimplePolymer,
  getConcatenateMolfilesVariables,
  getMixedStyleAttachmentErrorMessage,
  shouldDisableAttachButton,
  useAttachConjugateState,
  getPolymersForAttachmentOnEdit,
  connectionSideNotFullySpecified,
  getRelatedAttachmentStyle,
  validateConnectionSideForSubmission,
} from 'modbio/complex-polymers/tabs/ComplexPolymerAttachConjugateModalUtil';
import {
  ConjugateConnection,
  ConjugateConnectionMap,
} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsSummaryUtil';
import DrawnStyleAttachConjugateSection, {
  canRunConcatenateMolfilesQuery,
  CONCATENATE_MOLFILES_QUERY,
} from 'modbio/complex-polymers/tabs/DrawnStyleAttachment';
import LeavingGroupStyleAttachConjugateSection from 'modbio/complex-polymers/tabs/LeavingGroupStyleAttachment';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';

export const MOLECULE_ATTACHMENT_MODAL_QUERY = gql`
  query MoleculeAttachmentModalQuery($complexPolymerId: StrictString!, $polymerRef: StrictString!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    moleculeInComplexPolymer(complexPolymerId: $complexPolymerId, polymerRef: $polymerRef) {
      ...MoleculeInComplexPolymerGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('MoleculeInComplexPolymer').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const NUCLEOTIDE_ATTACHMENT_MODAL_QUERY = gql`
  query NucleotideAttachmentModalQuery(
    $complexPolymerId: StrictString
    $polymerRef: StrictString!
    $residueIndex: StrictInt!
    $complexPolymerType: GQLComplexPolymerType!
  ) @engComponent(name: MODIFIED_BIOLOGICS) @skipAudit {
    nucleotideInComplexPolymer(
      complexPolymerId: $complexPolymerId
      polymerRef: $polymerRef
      residueIndex: $residueIndex
      complexPolymerType: $complexPolymerType
    ) {
      ...NucleotideInComplexPolymerGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('NucleotideInComplexPolymer').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ComplexPolymerAttachConjugateModalProps {
  isEditMode: boolean;
  complexPolymerType: ComplexPolymerType;
  complexPolymerId: string;
  startingConjugateRef: string;
  simplePolymers: Array<SimplePolymerGraphQL>;
  conjugateConnectionMap: ConjugateConnectionMap;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  onRequestClose: () => void;
  onAddConnection: (
    conjugateConnection: ConjugateConnection,
    attachmentMolfile: string | null
  ) => Promise<void>;
}

export default function ComplexPolymerAttachConjugateModal({
  isEditMode,
  complexPolymerType,
  complexPolymerId,
  startingConjugateRef,
  simplePolymers,
  conjugateConnectionMap,
  resolvedAttachments,
  onRequestClose,
  onAddConnection,
}: ComplexPolymerAttachConjugateModalProps): JSX.Element {
  const attachConjugateState = useAttachConjugateState(
    isEditMode,
    startingConjugateRef,
    conjugateConnectionMap
  );

  const {connection, attachmentMolfile, attachmentMolfileError} = attachConjugateState;

  const attachmentStyle = connection.attachment_style!;

  const [bannerProps, setBannerProps] = useState<null | InlineBannerProps>(null);

  const buttons = [
    {
      text: 'Attach',
      disabled: shouldDisableAttachButton(connection, attachmentMolfile, attachmentMolfileError),
      tooltipTitle: attachmentMolfileError,
      submit: true,
    },
  ];

  const onRequestSubmit = async (): Promise<void> => {
    const complexPolymerDisplayName = COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType];
    LoadingScreen.show(`Updating ${complexPolymerDisplayName}...`);
    validateConnectionSideForSubmission(attachmentStyle, connection.source);
    validateConnectionSideForSubmission(attachmentStyle, connection.target);
    const conjugateConnection = conjugateConnectionMap[connection.target.polymer_ref];

    trackEvent(MODBIO_ANALYTICS_ACTIONS.ADDED_ATTACHMENT_IN_ATTACHMENT_MODAL, {
      action_origin: ActionOrigin.MODBIO_COMPLEX_POLYMERS,
      action_method: ActionMethod.BUTTON_CLICK,
    });

    try {
      await onAddConnection(
        {
          ...conjugateConnection,
          helmReference: connection.target.polymer_ref,
          residueIndex: connection.target.residue_index,
          rGroup:
            attachmentStyle === ComplexPolymerAttachmentStyle.LEAVING_GROUP
              ? connection.target.r_group
              : null,
          modificationType:
            getPolymerTypeFromRef(connection.target.polymer_ref) === HELMPolymerType.RNA
              ? connection.target.modification_type
              : null,
          linkedEntityResidueIndex: connection.source.residue_index ?? undefined,
          linkedEntityHelmReference: connection.source.polymer_ref,
          linkedEntityRGroup:
            attachmentStyle === ComplexPolymerAttachmentStyle.LEAVING_GROUP
              ? connection.source.r_group
              : null,
          linkedEntityModificationType: connection.source.modification_type,
          attachmentStyle,
        },
        attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN ? attachmentMolfile : null
      );
    } finally {
      onRequestClose();
      LoadingScreen.hide();
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit attachment' : 'Attach component'}
      large={true}
      buttons={buttons}
      onRequestClose={onRequestClose}
      scrollable={false}
      onRequestSubmit={onRequestSubmit}
      bodyPadded={false}
      showHeaderBorder={false}
      bannerProps={bannerProps}
    >
      <ComplexPolymerAttachConjugateSection
        isEditMode={isEditMode}
        complexPolymerType={complexPolymerType}
        conjugateConnectionMap={conjugateConnectionMap}
        resolvedAttachments={resolvedAttachments}
        attachConjugateState={attachConjugateState}
        forCreate={false}
        complexPolymerId={complexPolymerId}
        simplePolymers={getPolymersForAttachmentOnEdit(simplePolymers)}
        setBannerProps={setBannerProps}
      />
    </Modal>
  );
}

export interface ComplexPolymerAttachConjugateSectionProps {
  isEditMode: boolean;
  complexPolymerType: ComplexPolymerType;
  conjugateConnectionMap: ConjugateConnectionMap;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  attachConjugateState: AttachConjugateState;
  forCreate: boolean;
  onClickAttach?: (conjugateConnection: ConjugateConnection) => void;
  complexPolymerId?: string;
  simplePolymers: Array<SimplePolymer>;
  setBannerProps: (bannerProps: InlineBannerProps | null) => void;
}

export function ComplexPolymerAttachConjugateSection({
  isEditMode,
  complexPolymerType,
  conjugateConnectionMap,
  resolvedAttachments,
  attachConjugateState,
  forCreate,
  onClickAttach,
  complexPolymerId,
  simplePolymers,
  setBannerProps,
}: ComplexPolymerAttachConjugateSectionProps): JSX.Element {
  useEffect(() => {
    if (!connectionSideNotFullySpecified(attachConjugateState.connection.source)) {
      const relatedAttachmentStyle = getRelatedAttachmentStyle(
        attachConjugateState.connection.source,
        conjugateConnectionMap,
        attachConjugateState.connection.target.polymer_ref
      );
      // If we have a connection which is not of the same attachment style, update the connection state
      if (
        relatedAttachmentStyle != null &&
        relatedAttachmentStyle !== attachConjugateState.connection.attachment_style
      ) {
        setBannerProps({
          bannerType: InlineBannerType.ERROR,
          text: getMixedStyleAttachmentErrorMessage(relatedAttachmentStyle),
          onDismiss: () => {
            setBannerProps(null);
          },
        });
        attachConjugateState.setConnection({
          ...attachConjugateState.connection,
          attachment_style: relatedAttachmentStyle,
          source: {
            ...attachConjugateState.connection.source,
            r_group: null,
          },
          target: {
            ...attachConjugateState.connection.target,
            r_group: null,
          },
        });
      }
    }
  });

  const currentConjugateConnection =
    conjugateConnectionMap[attachConjugateState.connection.target.polymer_ref];

  const concatMolfilesVariables = getConcatenateMolfilesVariables(
    simplePolymers,
    attachConjugateState.connection,
    resolvedAttachments
  );
  return (
    <TabContainer
      activeTab={attachConjugateState.connection.attachment_style!}
      onRequestTabChange={(attachmentStyle: ComplexPolymerAttachmentStyle) => {
        trackEvent(MODBIO_ANALYTICS_ACTIONS.TOGGLED_ATTACHMENT_STYLE_IN_ATTACHMENT_MODAL, {
          action_origin: ActionOrigin.MODBIO_COMPLEX_POLYMERS,
          action_method: ActionMethod.BUTTON_CLICK,
          complex_polymer_attachment_style: attachmentStyle,
        });
        attachConjugateState.setConnection({
          ...attachConjugateState.connection,
          attachment_style: attachmentStyle,
          source: {
            ...attachConjugateState.connection.source,
            r_group: null,
          },
          target: {
            ...attachConjugateState.connection.target,
            r_group: null,
          },
        });
      }}
      className={forCreate ? css(styles.createTabContainer) : css(styles.editTabContainer)}
      paneClassName={css(styles.pane)}
      navClassName={css(styles.nav)}
    >
      <Tab tabKey={ComplexPolymerAttachmentStyle.DRAWN} title="Draw">
        <BenchlingQuery<ConcatenateMolfilesQuery>
          query={CONCATENATE_MOLFILES_QUERY}
          renderLoading={() => <CenteredHorizontalThrobber />}
          variables={concatMolfilesVariables}
          fetchPolicy="network-only"
          // This will happen if the user is making a connection between two nucleotide sequences, but has not
          // selected a residue index/monomer on either of them yet.
          skip={!canRunConcatenateMolfilesQuery(concatMolfilesVariables)}
        >
          {(queryResult) => (
            <DrawnStyleAttachConjugateSection
              complexPolymerId={complexPolymerId}
              complexPolymerType={complexPolymerType}
              conjugateConnectionMap={conjugateConnectionMap}
              attachConjugateState={attachConjugateState}
              molfileOrError={
                queryResult != null
                  ? queryResult.concatenateMolfiles.molfile
                  : {__typename: 'StringPropertyNode', value: EMPTY_MOLFILE}
              }
              forCreate={forCreate}
              onClickAttach={onClickAttach}
              simplePolymers={simplePolymers}
            />
          )}
        </BenchlingQuery>
      </Tab>
      <Tab tabKey={ComplexPolymerAttachmentStyle.LEAVING_GROUP} title="Select">
        <LeavingGroupStyleAttachConjugateSection
          isEditMode={isEditMode}
          complexPolymerType={complexPolymerType}
          conjugateConnectionMap={conjugateConnectionMap}
          attachConjugateState={attachConjugateState}
          forCreate={forCreate}
          onClickAttach={onClickAttach}
          currentConjugateConnection={currentConjugateConnection}
          complexPolymerId={complexPolymerId}
          simplePolymers={simplePolymers}
        />
      </Tab>
    </TabContainer>
  );
}

const TAB_CONTAINER_STYLES: StyleDeclarationMap | CSSProperties = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

const styles = StyleSheet.create({
  editTabContainer: {
    ...TAB_CONTAINER_STYLES,
    minHeight: '500px',
  },
  createTabContainer: {
    ...TAB_CONTAINER_STYLES,
    marginTop: 10,
  },
  pane: {
    marginTop: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    marginLeft: 17,
    marginRight: 0,
  },
});
